import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [registrationStatus, setRegistrationStatus] = useState(null);

  useEffect(() => {
    if (token) {
      fetchCurrentUser();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchCurrentUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setUser(userData);
    setRegistrationStatus(null);
    return userData;
  };

  const register = async (userData) => {
    const response = await axios.post(`${API}/auth/register`, userData);
    const { token: newToken, user: newUser, status } = response.data;
    
    // Only set token and user if status is 'active' (admin self-registration)
    if (status === 'active' && newToken) {
      localStorage.setItem('token', newToken);
      setToken(newToken);
      setUser(newUser);
    } else {
      // For pending users, store registration status
      setRegistrationStatus({ status, user: newUser });
    }
    
    return { user: newUser, status };
  };

  const checkRegistrationStatus = async (email) => {
    try {
      const response = await axios.get(`${API}/auth/status?email=${encodeURIComponent(email)}`);
      return response.data;
    } catch (error) {
      return null;
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    setRegistrationStatus(null);
    // Navigate to home page is handled in DashboardLayout
  };

  const clearRegistrationStatus = () => {
    setRegistrationStatus(null);
  };

  return (
    <AuthContext.Provider value={{ 
      user, token, login, register, logout, loading, 
      registrationStatus, checkRegistrationStatus, clearRegistrationStatus 
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export default AuthContext;